import { Message } from '@/enums/message.enum';
import { createAppSlice } from '../createAppSlice';
import { MessageType } from '@/types/common';
import { MESSAGE } from '@/constants/message.constant';

export type NotificationState = {
  loading: boolean;
  notificationList: NotificationItem[];
  notificationDetail: NotificationItem | null;
  emailNotificationDetail: EmailNotificationItem | null;
  detailLoading: boolean;
  message?: MessageType;
  pagination: Pagination;
  frequencyTimes: FrequencyTimeItem[];
  linkFunctions: LinkFunctionItem[];
  weekDays: WeekDayItem[];
  targetGroups: TargetGroupItem[];
  referenceDataLoading: boolean;
};

const initialState: NotificationState = {
  loading: false,
  notificationList: [],
  notificationDetail: null,
  emailNotificationDetail: null,
  detailLoading: false,
  message: undefined,
  pagination: { curPage: 1, limitPage: 10, totalRows: 0, totalPage: 0 },
  frequencyTimes: [],
  linkFunctions: [],
  weekDays: [],
  targetGroups: [],
  referenceDataLoading: false
};

export const notificationSlice = createAppSlice({
  name: 'notifications',
  initialState,
  reducers: (create) => ({
    getFrequencyTimes: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('notifications/frequency-times');
        return response.data;
      },
      {
        pending: (state) => {
          state.referenceDataLoading = true;
        },
        fulfilled: (state, action: { payload: FrequencyTimeItem[] }) => {
          state.frequencyTimes = action.payload;
          state.referenceDataLoading = false;
        },
        rejected: (state) => {
          state.referenceDataLoading = false;
        }
      }
    ),

    getLinkFunctions: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('notifications/link-functions');
        return response.data;
      },
      {
        pending: (state) => {
          state.referenceDataLoading = true;
        },
        fulfilled: (state, action: { payload: LinkFunctionItem[] }) => {
          state.linkFunctions = action.payload;
          state.referenceDataLoading = false;
        },
        rejected: (state) => {
          state.referenceDataLoading = false;
        }
      }
    ),

    getWeekDays: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('notifications/week-days');
        return response.data;
      },
      {
        pending: (state) => {
          state.referenceDataLoading = true;
        },
        fulfilled: (state, action: { payload: WeekDayItem[] }) => {
          state.weekDays = action.payload;
          state.referenceDataLoading = false;
        },
        rejected: (state) => {
          state.referenceDataLoading = false;
        }
      }
    ),

    getTargetGroups: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('notifications/target-groups');
        return response.data;
      },
      {
        pending: (state) => {
          state.referenceDataLoading = true;
        },
        fulfilled: (state, action: { payload: TargetGroupItem[] }) => {
          state.targetGroups = action.payload;
          state.referenceDataLoading = false;
        },
        rejected: (state) => {
          state.referenceDataLoading = false;
        }
      }
    ),

    getListNotification: create.asyncThunk(
      async ({
        filter,
        currentPage = 1,
        limitPage = 10
      }: {
        filter?: Partial<FilterNotification>;
        currentPage?: number;
        limitPage?: number;
      }) => {
        const payload = {
          ...filter,
          PAGE: currentPage,
          LIMIT: limitPage
        };
        const response = await apiFetcher.post('notifications/list', payload);
        return response.data;
      },
      {
        pending: (state: NotificationState) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (
          state: NotificationState,
          action: {
            payload: { listData: NotificationItem[]; pagination: Pagination };
          }
        ) => {
          state.notificationList = action.payload.listData;
          state.pagination = action.payload.pagination;
          state.loading = false;
        },
        rejected: (
          state: NotificationState,
          action: { error?: { message?: string; code?: string } }
        ) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message:
              action.error?.message ?? MESSAGE.GET_LIST_NOTIFICATION_FAIL,
            ...(action.error?.code && {
              description: `Error code: ${action.error.code}`
            })
          };
        }
      }
    ),

    getNotificationDetail: create.asyncThunk(
      async (id: number) => {
        const response = await apiFetcher.get(`notifications/web-app/${id}`);
        return response.data;
      },
      {
        pending: (state) => {
          state.detailLoading = true;
          state.message = undefined;
        },
        fulfilled: (state, action) => {
          state.detailLoading = false;
          state.notificationDetail = action.payload;
        },
        rejected: (state, action) => {
          state.detailLoading = false;
          state.message = {
            type: Message.ERROR,
            message:
              action.error?.message ?? MESSAGE.GET_NOTIFICATION_DETAIL_FAIL,
            ...(action.error?.code && {
              description: `Error code: ${action.error.code}`
            })
          };
        }
      }
    ),
    createNotification: create.asyncThunk(
      async (data: CreateNotificationPayload) => {
        const response = await apiFetcher.post('notifications/web-app', data);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_NOTIFICATION_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: action.error?.message ?? MESSAGE.CREATE_NOTIFICATION_FAIL,
            ...(action.error?.code && {
              description: `Error code: ${action.error.code}`
            })
          };
        }
      }
    ),
    updateNotification: create.asyncThunk(
      async (data: UpdateNotificationPayload) => {
        const res = await apiFetcher.put('notifications/web-app', data);
        return res.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_NOTIFICATION_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: action.error?.message ?? MESSAGE.UPDATE_NOTIFICATION_FAIL,
            ...(action.error?.code && {
              description: `Error code: ${action.error.code}`
            })
          };
        }
      }
    ),
    deleteNotification: create.asyncThunk(
      async (id: number) => {
        const response = await apiFetcher.delete(`notifications/web-app/${id}`);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },

        fulfilled: (state: NotificationState, action) => {
          state.loading = false;
          state.notificationList = state.notificationList.filter(
            (item: NotificationItem) => {
              return item.ID !== action.meta.arg;
            }
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_NOTIFICATION_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: action.error?.message ?? MESSAGE.DELETE_NOTIFICATION_FAIL,
            ...(action.error?.code && {
              description: `Error code: ${action.error.code}`
            })
          };
        }
      }
    ),

    resetMessageNotification: create.reducer((state) => {
      state.message = undefined;
    }),

    resetNotificationDetail: create.reducer((state) => {
      state.notificationDetail = null;
      state.emailNotificationDetail = null;
      state.detailLoading = false;
    }),

    getEmailNotificationDetail: create.asyncThunk(
      async (id: number) => {
        const response = await apiFetcher.get(`notifications/email/${id}`);
        return response.data;
      },
      {
        pending: (state) => {
          state.detailLoading = true;
          state.message = undefined;
        },
        fulfilled: (state, action: { payload: EmailNotificationItem }) => {
          state.detailLoading = false;
          state.emailNotificationDetail = action.payload;
        },
        rejected: (state, action) => {
          state.detailLoading = false;
          state.message = {
            type: Message.ERROR,
            message:
              action.error?.message ?? MESSAGE.GET_EMAIL_NOTIFICATION_DETAIL_FAIL,
            ...(action.error?.code && {
              description: `Error code: ${action.error.code}`
            })
          };
        }
      }
    ),

    createEmailNotification: create.asyncThunk(
      async (data: CreateEmailNotificationPayload) => {
        const response = await apiFetcher.post('notifications/email', data);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_EMAIL_NOTIFICATION_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: action.error?.message ?? MESSAGE.CREATE_EMAIL_NOTIFICATION_FAIL,
            ...(action.error?.code && {
              description: `Error code: ${action.error.code}`
            })
          };
        }
      }
    ),

    updateEmailNotification: create.asyncThunk(
      async (data: UpdateEmailNotificationPayload) => {
        const res = await apiFetcher.put('notifications/email', data);
        return res.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_EMAIL_NOTIFICATION_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message:
              action.error?.message ?? MESSAGE.UPDATE_EMAIL_NOTIFICATION_FAIL,
            ...(action.error?.code && {
              description: `Error code: ${action.error.code}`
            })
          };
        }
      }
    ),

    deleteEmailNotification: create.asyncThunk(
      async (id: number) => {
        const response = await apiFetcher.delete(`notifications/email/${id}`);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state: NotificationState, action) => {
          state.loading = false;
          state.notificationList = state.notificationList.filter(
            (item: NotificationItem) => {
              return item.ID !== action.meta.arg;
            }
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_EMAIL_NOTIFICATION_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: action.error?.message ?? MESSAGE.DELETE_EMAIL_NOTIFICATION_FAIL,
            ...(action.error?.code && {
              description: `Error code: ${action.error.code}`
            })
          };
        }
      }
    )
  }),

  selectors: {
    watchNotificationList: (state) => state.notificationList,
    watchLoadingNotification: (state) => state.loading,
    watchNotificationDetail: (state) => state.notificationDetail,
    watchEmailNotificationDetail: (state) => state.emailNotificationDetail,
    watchDetailLoading: (state) => state.detailLoading,
    watchMessageNotification: (state) => state.message,
    watchPaginationNotification: (state) => state.pagination,
    watchFrequencyTimes: (state) => state.frequencyTimes,
    watchLinkFunctions: (state) => state.linkFunctions,
    watchWeekDays: (state) => state.weekDays,
    watchTargetGroups: (state) => state.targetGroups,
    watchReferenceDataLoading: (state) => state.referenceDataLoading
  }
});

export const {
  watchNotificationList,
  watchLoadingNotification,
  watchNotificationDetail,
  watchEmailNotificationDetail,
  watchDetailLoading,
  watchMessageNotification,
  watchPaginationNotification,
  watchFrequencyTimes,
  watchLinkFunctions,
  watchWeekDays,
  watchTargetGroups,
  watchReferenceDataLoading
} = notificationSlice.selectors;

export const {
  getListNotification,
  getNotificationDetail,
  resetMessageNotification,
  resetNotificationDetail,
  createNotification,
  updateNotification,
  deleteNotification,
  getFrequencyTimes,
  getLinkFunctions,
  getWeekDays,
  getTargetGroups,
  getEmailNotificationDetail,
  createEmailNotification,
  updateEmailNotification,
  deleteEmailNotification
} = notificationSlice.actions;
